import React from 'react';
import styled, { css } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';

import { Typography } from 'src/typography';
import Skeleton from 'src/components/core/loading-skeletons/loading-skeleton';
import { ModalExitIcon } from 'src/components/core';
import { useLoyaltyPassImpressionTracking } from 'src/components/loyalty/use-loyalty-pass-impressions';
import { NotificationIcon } from 'src/assets/loyalty-pass/notification-icon';
import { RewardsBalanceIcon } from 'src/assets/loyalty-pass/rewards-balance-icon';

type LoyaltyPassQRCodeProps = {
  url: string | null;
  loading: boolean;
  onClose: () => void;
  variant: 'bank-account' | 'loyalty';
};

const LOYALTY_HEADER = 'Add loyalty pass to your wallet & never miss out on rewards';
const BANK_ACCOUNT_HEADER = 'Add loyalty pass to your wallet';

export const LoyaltyPassQRCode = ({ url, loading, onClose, variant }: LoyaltyPassQRCodeProps): JSX.Element => {
  const { ref } = useLoyaltyPassImpressionTracking({ type: 'qrCode' });

  const headerText = variant === 'loyalty' ? LOYALTY_HEADER : BANK_ACCOUNT_HEADER;

  const closeButtonText = variant === 'loyalty' ? 'Not right now' : 'Close';
  const size = variant === 'loyalty' ? 200 : 230;
  return (
    <Container>
      <ModalExitIcon onClick={onClose} />
      <HeaderTitle id='loyalty-pass-title' size='small' tag='h2' weight='semi'>
        {headerText}
      </HeaderTitle>
      <QrCodeWrapper>
        <QrCodeContainer ref={ref}>
          {!!url && !loading && (
            <QRCodeSVG
              data-testid='loyalty-pass-qr-code'
              value={url}
              size={size}
              level='H'
              aria-label='QR code to add loyalty pass to Apple Wallet'
              imageSettings={{
                src: '/images/apple-wallet-icon.png',
                height: 48,
                width: 48,
                excavate: true,
              }}
            />
          )}
          {loading && <Skeleton width={size} height={size} rounded />}
          <QRLabel id='loyalty-pass-instructions' size='small' tag='h3' weight='bold'>
            SCAN TO ADD PASS
          </QRLabel>
        </QrCodeContainer>

        {variant === 'loyalty' && (
          <BenefitsRow>
            <BenefitItem>
              <BenefitIcon>
                <RewardsBalanceIcon />
              </BenefitIcon>
              <BenefitText size='large' tag='p' weight='regular'>
                Access your rewards balance anytime
              </BenefitText>
            </BenefitItem>
            <BenefitItem>
              <BenefitIcon>
                <NotificationIcon />
              </BenefitIcon>
              <BenefitText size='large' tag='p' weight='regular'>
                Get notified of exclusive deals & events
              </BenefitText>
            </BenefitItem>
          </BenefitsRow>
        )}
      </QrCodeWrapper>

      <CloseButton onClick={onClose} aria-label='Close loyalty pass modal'>
        {closeButtonText}
      </CloseButton>
    </Container>
  );
};

const Container = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[6]};
    width: 100%;
  `}
`;

const HeaderTitle = styled(Typography.Heading)`
  align-self: stretch;
  line-height: 1.2;
  padding: ${({ theme: { spaces } }) => `0 ${spaces[6]}`};
`;

const QrCodeWrapper = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[4]};
    align-self: stretch;
  `}
`;

const QrCodeContainer = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    display: flex;
    padding: ${spaces[1]};
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${spaces[1]};
    border-radius: ${spaces[1]};
    border: 2.5px solid ${colors.grey[95]};
    background: ${colors.white};
  `}
`;

const QRLabel = styled(Typography.Heading)`
  ${({ theme: { colors } }) => css`
    color: ${colors.grey[30]};
    line-height: 28px;
  `}
`;

const CloseButton = styled.button`
  ${({ theme: { colors } }) => css`
    background: none;
    border: none;
    color: ${colors.blue[50]};
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.065px;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  `}
`;

const BenefitsRow = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    padding-bottom: ${spaces[2]};
    justify-content: center;
    align-items: flex-start;
    gap: ${spaces[2]};
    align-self: stretch;
  `}
`;

const BenefitItem = styled.div`
  ${({ theme: { spaces } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${spaces[2]};
  `}
`;

const BenefitIcon = styled.div`
  ${({ theme: { spaces, colors } }) => css`
    display: flex;
    padding: ${spaces[2]};
    justify-content: center;
    align-items: center;
    border-radius: 80px;
    background: ${colors.blue[90]};
  `}
`;

const BenefitText = styled(Typography.Body)`
  line-height: 1.2;
`;
